/**
 * AI CONTEXT
 * Purpose: maps filtered project docs into table rows with search + sort.
 * Uses: semester/search/sort settings + Project mapper class.
 * Used by: ProjectsTable.
 * Importance: project listing data source.
 * Refactor: align readiness semantics with other table hooks (avoid hardcoded isReady true).
 */
import { useMemo } from "react"

import { projectAtoms } from "#atoms"
import { Project } from "#classes/Project"
import { USER_SETTINGS } from "#constants/globalConstants"
import {
  getProjectSortValue,
  PROJECT_SEARCHABLE_FIELDS,
  PROJECT_SORTABLE_FIELDS,
} from "#constants/projectOptions"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  ProjectConstructor,
  ProjectForTable,
} from "#customTypes/composedTypes/projectTypes"
import { SETTINGS_KEYS } from "#customTypes/globalTypes"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import useOptimizedSearch from "#hooks/newHooks/useOptimizedSearch"

import { useTableSort } from "./useTableSort"

const useProjectTableData = () => {
  const { getUserSetting } = useUserContext()
  const filterSemester = getUserSetting<string>(
    USER_SETTINGS.PROJECTS_TABLE_SEMESTER
  )
  const resultsCount = getUserSetting<number>(
    USER_SETTINGS.PROJECTS_TABLE_RESULTS_COUNT
  )
  const defaultSearchTerm = getUserSetting<string>(
    USER_SETTINGS.PROJECTS_TABLE_SEARCH_TERM
  )

  const { sortField, sortOrder, sortRows, onSortChange } =
    useTableSort<ProjectForTable>({
      tableKey: SETTINGS_KEYS.PROJECTS,
      sortableFields: PROJECT_SORTABLE_FIELDS,
      getSortValue: getProjectSortValue,
    })

  const query: FirebaseQueryConfig = useMemo(() => {
    if (filterSemester === "All") return {}

    return {
      queries: {
        semester: {
          comparator: COMPARATORS.EQUALS,
          value: filterSemester,
        },
      },
    }
  }, [filterSemester])

  const projectsFromDb = useFilteredDocs<ProjectConstructor>(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    query
  )

  const baseRows = useMemo(() => {
    if (!projectsFromDb?.length) return []

    return projectsFromDb.map((project) => ({
      ...new Project(project).getProjectForTable(),
      lastUpdatedAt: project.lastUpdatedAt,
    }))
  }, [projectsFromDb])

  const sortedRows = useMemo(() => sortRows(baseRows), [sortRows, baseRows])

  const { filteredItems: filteredProjects, onSearch } =
    useOptimizedSearch<ProjectForTable>(
      sortedRows,
      PROJECT_SEARCHABLE_FIELDS,
      resultsCount,
      defaultSearchTerm
    )

  const projectsForTable = useMemo(
    () => sortRows(filteredProjects),
    [sortRows, filteredProjects]
  )

  return {
    projectsForTable,
    onSearch,
    isReady: true, // Jotai data is always ready
    errors: [], // Errors are handled at the subscription level
    sortField,
    sortOrder,
    onSortChange,
  }
}

export default useProjectTableData
